import { Injectable, NgZone } from "@angular/core";
import { ContentType } from "../shared/contentTypes";
import { CoreLogService } from "./log.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { ObjectObserverFactory } from "./objectObserverFactory";
import {
  CheckoutPaymentMethods,
  CheckoutPaymentMethodsModel,
  CheckoutApplePaymentSession,
  CheckoutApplePaymentSessionModel,
  CheckoutPaymentModel,
  CheckoutPayment,
  CheckoutPaymentDetails,
  GenerateOriginKeyModel,
  GetStoredCheckoutPaymentModel,
  ResponseCode_Done,
  CheckoutOriginKey,
  GetPublicKeyModel,
  InHouseDataModel,
} from "../shared/models/checkout.model";
import { CoreLanguageService } from "./language.service";
import { CoreCOMService } from "./com.service";
import { ClientType } from "./com/comEnvironmentClient";
import {
  AddPurchaseOrderDataModel,
  AddPurchaseOrderItemDataModel,
  FurtherAddressViewModel,
  orderTypes,
} from "../shared/models/webshop.model";
import { LocalKeys, StorageHive } from "./valueStorage.service";
import { Router } from "@angular/router";
import { RouterHelperService } from "./routerHelper.service";
import { UappErrorCode } from "./http.service";
import { WebShopService } from "./webShop.service";

export class LastOrderInfo {
  /** Order Reference */
  id: string;
  /** Order Token exists only if order is not inHouse */
  token: string;
  /** Order Type */
  ordertype: orderTypes;
  /** Standort */
  location: number;
  /** QR-Code */
  qrCode: string;
  /** Jetzt bezahlen ist Pflicht */
  mandatory_Payment: boolean;
  /** Standort Telefonnummer */
  locTele: string;
  /** Abholort */
  handoverStreet: string;
  /** Abholzeitpunkt YMD */
  handoverDate: number; // yyyyMMdd
  /** Abholzeitpunkt HMS */
  handoverTime: number; // hhmmss
  /** Gast E-Mail */
  customerMail: string;
  /** Lieferadresse */
  deliveryAddressViewModel: FurtherAddressViewModel;
  /** ??? */
  fixedBasket: boolean;
  /** ??? */
  returnURLSuccess: string;
  /** ??? */
  returnURLFail: string;

  orderedItems: AddPurchaseOrderItemDataModel[]

  group_id?: number; // group id for group orders
}

@Injectable({
  providedIn: "root",
})
export class CheckoutService {
  constructor(
    private _coreLogService: CoreLogService,
    private _coreCOMService: CoreCOMService,
    private _coreLanguageService: CoreLanguageService,
    private _ngZone: NgZone,
    private _router: Router,
    private _routerHelperService: RouterHelperService,
    private _objObsInjectHelper: CircularInjectionHelper,
    private _webShopService: WebShopService
  ) { }

  private GetLocale(): string {
    return this._coreLanguageService.getCurrentLanguage(); // todo get locale als "de-DE" ?
  }

  private GetDevice(): string {
    const me = this;
    if (me._coreCOMService.environment.client.type === ClientType.android)
      return "android";
    else if (me._coreCOMService.environment.client.type === ClientType.ios)
      return "ios";
    // if (me._coreCOMService.environment.client.type === ClientType.browser)
    else return "browser";
  }

  private GetChannel(): string {
    const me = this;
    if (me._coreCOMService.environment.client.type === ClientType.android)
      return "Android";
    else if (me._coreCOMService.environment.client.type === ClientType.ios)
      return "iOS";
    // if (me._coreCOMService.environment.client.type === ClientType.browser)
    else return "Web";
  }

  /**
   * Ermittelt vom Server zur Bestellung passende Zahlungsformen
   * @param order order what will be payed (it not stored)
   */
  public GetPaymentMethodsFromServer(
    order: AddPurchaseOrderDataModel,
    ih_data: InHouseDataModel
  ): Promise<CheckoutPaymentMethodsModel> {
    const me = this;
    return new Promise<CheckoutPaymentMethodsModel>((resolve, reject) => {
      try {
        const RequestData = new CheckoutPaymentMethods();
        RequestData.locale = me.GetLocale();
        RequestData.device = me.GetDevice();
        RequestData.channel = me.GetChannel();
        RequestData.order = order;
        RequestData.ih_data = ih_data;
        RequestData.with_dropin_config = true; // need config for Adyen DropIn
        const FunctionName = "checkout/GetPaymentMethods";
        const SyncDataModelConstructor = CheckoutPaymentMethodsModel;
        me._objObsInjectHelper.yourAppServer.Api.RequestService(
          SyncDataModelConstructor,
          FunctionName,
          RequestData
        )
          .then((response) => {
            if (response.OK) {
              me._coreLogService.debug(
                "CheckoutService.GetPaymentMethodsFromServer",
                "RequestService (response OK)",
                response
              );
              resolve(response.Data);
            } else {
              me._coreLogService.error(
                "CheckoutService.GetPaymentMethodsFromServer",
                "RequestService (response not OK)",
                response
              );
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => {
            me._coreLogService.error(
              "CheckoutService.GetPaymentMethodsFromServer",
              "RequestService",
              error
            );
            reject();
          });
      } catch (error) {
        me._coreLogService.error(
          "CheckoutService.GetPaymentMethodsFromServer",
          "try catch",
          error
        );
        reject();
      }
    });
  }

  public GenerateOriginKey(origin: string): Promise<GenerateOriginKeyModel> {
    const me = this;
    return new Promise<GenerateOriginKeyModel>((resolve, reject) => {
      try {
        const RequestData = new CheckoutOriginKey();
        RequestData.origin = origin;
        const FunctionName = "checkout/GenerateOriginKey";
        const SyncDataModelConstructor = GenerateOriginKeyModel;
        me._objObsInjectHelper.yourAppServer.Api.RequestService(
          SyncDataModelConstructor,
          FunctionName,
          RequestData
        )
          .then((response) => {
            if (response.OK) {
              me._coreLogService.debug(
                "CheckoutService.GenerateOriginKey",
                "RequestService (response OK)",
                response
              );
              resolve(response.Data);
            } else {
              me._coreLogService.error(
                "CheckoutService.GenerateOriginKey",
                "RequestService (response not OK)",
                response
              );
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => {
            me._coreLogService.error(
              "CheckoutService.GenerateOriginKey",
              "RequestService",
              error
            );
            reject();
          });
      } catch (error) {
        me._coreLogService.error(
          "CheckoutService.GenerateOriginKey",
          "try catch",
          error
        );
        reject();
      }
    });
  }

  /** Gets the Adyen PublicKey for DropIn */
  public GetPublicKey(): Promise<GetPublicKeyModel> {
    const me = this;
    return new Promise<GetPublicKeyModel>((resolve, reject) => {
      try {
        const RequestData = null; // GetPublicKey dont need request data
        const FunctionName = "checkout/GetPublicKey";
        const SyncDataModelConstructor = GetPublicKeyModel;
        me._objObsInjectHelper.yourAppServer.Api.RequestService(
          SyncDataModelConstructor,
          FunctionName,
          RequestData
        )
          .then((response) => {
            if (response.OK) {
              me._coreLogService.debug(
                "CheckoutService.GetPublicKey",
                "RequestService (response OK)",
                response
              );
              resolve(response.Data);
            } else {
              me._coreLogService.error(
                "CheckoutService.GetPublicKey",
                "RequestService (response not OK)",
                response
              );
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => {
            me._coreLogService.error(
              "CheckoutService.GetPublicKey",
              "RequestService",
              error
            );
            reject();
          });
      } catch (error) {
        me._coreLogService.error(
          "CheckoutService.GetPublicKey",
          "try catch",
          error
        );
        reject();
      }
    });
  }

  /**
   * Make the payment for selectet type
   * @param type choose one of the possible types
   * @param order order that will be payed
   * @param ih_data inHouse data on InHouse order
   * @param data data is inviduall for payment type ('adyne' need dropin_status)
   */
  public MakePaymentOnServer(
    type:
      | "adyen"
      | "wallee"
      | "hs_webvoucher"
      | "hs_pay_at_waiter"
      | "hs_cash"
      | "hs_cashless"
      | "hs_accounting"
      | "hs_accounting_cus"
      | "hs_paypal",
    order: AddPurchaseOrderDataModel,
    ih_data: InHouseDataModel,
    data: any
  ): Promise<CheckoutPaymentModel> {
    const me = this;
    return new Promise<CheckoutPaymentModel>((resolve, reject) => {
      try {
        const RequestData = new CheckoutPayment();
        RequestData.locale = me.GetLocale();
        RequestData.device = me.GetDevice();
        RequestData.channel = me.GetChannel();
        RequestData.order = order;
        RequestData.ih_data = ih_data;
        if (me._coreCOMService.environment.client.type === ClientType.browser) {
          RequestData.return_url = location.origin; // damit Bestellungen über Kundeneigene Domains möglich sind
        } else {
          RequestData.return_url = ""; // apps nutzen weiter die *.myhypersoftapp.de
        }
        RequestData.return_url_params = ""; // like: "test=1234&test2=987" (momentan nicht notwendig)
        RequestData.data_json = JSON.stringify(data);
        var FunctionName = "checkout/";
        switch (type) {
          case "adyen":
            // need data
            FunctionName += "MakeAdyenPayment";
            break;
          case "wallee":
            FunctionName += "PrepareWalleeCompletion";
            break;
          case "hs_webvoucher":
            FunctionName += "MakeWebvoucherPayment";
            break;
          case "hs_pay_at_waiter":
            // ist wie barzahlung bei abholung, nur der Order-Bon wird noch nicht abgeschlossen
            FunctionName += "MakeCashPayment";
            break;
          case "hs_cash":
            FunctionName += "MakeCashPayment";
            break;
          case "hs_cashless":
            FunctionName += "MakeCashlessPayment";
            break;
          case "hs_accounting":
            FunctionName += "MakeAccountingPayment";
            break;
          case "hs_accounting_cus":
            FunctionName += "MakeAccountingCustomerPayment";
            break;
          case "hs_paypal":
            FunctionName += "MakePayPalPayment";
            break;
          default:
            me._coreLogService.error(
              "CheckoutService.MakePaymentOnServer",
              "Unknown type",
              type
            );
            FunctionName += "MakeAdyenPayment"; // try adyen payment
            break;
        }
        const SyncDataModelConstructor = CheckoutPaymentModel;
        me._objObsInjectHelper.yourAppServer.Api.RequestService(
          SyncDataModelConstructor,
          FunctionName,
          RequestData
        )
          .then((response) => {
            if (response.OK) {
              me._coreLogService.debug(
                "CheckoutService.MakePaymentOnServer",
                "RequestService (response OK)",
                response
              );
              // todo use for get points web_voucher.service.parseNewBonusPointsFromBonusPointString
              const result: CheckoutPaymentModel = response.Data;
              result.is_remote_group_order = order.group_id && order.group_id > 0;
              resolve(result);
            } else {
              me._coreLogService.error(
                "CheckoutService.MakePaymentOnServer",
                "RequestService (response not OK)",
                response
              );
              if (response.HttpResponse.ya_error_code == UappErrorCode.INTERNAL_ERROR) {
                reject(response.HttpResponse.body.message);
              } else {
                reject(response.HttpResponse.codeText);
              }
            }
          })
          .catch((error) => {
            me._coreLogService.error(
              "CheckoutService.MakePaymentOnServer",
              "RequestService",
              error
            );
            reject();
          });
      } catch (error) {
        me._coreLogService.error(
          "CheckoutService.MakePaymentOnServer",
          "try catch",
          error
        );
        reject();
      }
    });
  }

  public MakeAdyenPaymentDetailsOnServer(
    location: number,
    order_id: string,
    ih_data: InHouseDataModel,
    dropin_data: any
  ): Promise<CheckoutPaymentModel> {
    const me = this;
    return new Promise<CheckoutPaymentModel>((resolve, reject) => {
      try {
        const RequestData = new CheckoutPaymentDetails();
        RequestData.location = location;
        RequestData.order_id = order_id;
        RequestData.ih_data = ih_data;
        RequestData.details_json = JSON.stringify(dropin_data.details);

        const FunctionName = "checkout/MakeAdyenPaymentDetails";
        const SyncDataModelConstructor = CheckoutPaymentModel;
        me._objObsInjectHelper.yourAppServer.Api.RequestService(
          SyncDataModelConstructor,
          FunctionName,
          RequestData
        )
          .then((response) => {
            if (response.OK) {
              me._coreLogService.debug(
                "CheckoutService.MakeAdyenPaymentDetailsOnServer",
                "RequestService (response OK)",
                response
              );
              resolve(response.Data);
            } else {
              me._coreLogService.error(
                "CheckoutService.MakeAdyenPaymentDetailsOnServer",
                "RequestService (response not OK)",
                response
              );
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => {
            me._coreLogService.error(
              "CheckoutService.MakeAdyenPaymentDetailsOnServer",
              "RequestService",
              error
            );
            reject();
          });
      } catch (error) {
        me._coreLogService.error(
          "CheckoutService.MakeAdyenPaymentDetailsOnServer",
          "try catch",
          error
        );
        reject();
      }
    });
  }

  public GetApplePaySessionFromServer(
    location: number,
    validationURL: string
  ): Promise<CheckoutApplePaymentSessionModel> {
    const me = this;
    return new Promise<CheckoutApplePaymentSessionModel>((resolve, reject) => {
      try {
        const RequestData = new CheckoutApplePaymentSession();
        RequestData.location = location;
        RequestData.validation_url = validationURL;
        const FunctionName = "checkout/GenerateApplePaySession";
        const SyncDataModelConstructor = CheckoutApplePaymentSessionModel;
        me._objObsInjectHelper.yourAppServer.Api.RequestService(
          SyncDataModelConstructor,
          FunctionName,
          RequestData
        )
          .then((response) => {
            if (response.OK) {
              me._coreLogService.debug(
                "CheckoutService.GetApplePaySessionFromServer",
                "RequestService (response OK)",
                response
              );
              resolve(response.Data);
            } else {
              me._coreLogService.error(
                "CheckoutService.GetApplePaySessionFromServer",
                "RequestService (response not OK)",
                response
              );
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => {
            me._coreLogService.error(
              "CheckoutService.GetApplePaySessionFromServer",
              "RequestService",
              error
            );
            reject();
          });
      } catch (error) {
        me._coreLogService.error(
          "CheckoutService.GetApplePaySessionFromServer",
          "try catch",
          error
        );
        reject();
      }
    });
  }

  public GetStoredCheckoutPaymentModel(
    token: string,
    order_id: string
  ): Promise<CheckoutPaymentModel> {
    const me = this;
    return new Promise<CheckoutPaymentModel>((resolve, reject) => {
      try {
        const RequestData = new GetStoredCheckoutPaymentModel();
        // Je nach Betriebssystem und Browser kann es vorkommen, dass der Token mehrfach encoded ist.
        // => Solange decoden, bis der Token sich nicht mehr ändert.
        let tokenDecode: string = decodeURIComponent(token);
        while (tokenDecode != token) {
          tokenDecode = decodeURIComponent(token);
          token = tokenDecode;
        }
        RequestData.token = token;
        RequestData.order_id = order_id;
        const FunctionName = "checkout/GetStoredCheckoutPaymentModel";
        const SyncDataModelConstructor = CheckoutPaymentModel;
        me._objObsInjectHelper.yourAppServer.Api.RequestService(
          SyncDataModelConstructor,
          FunctionName,
          RequestData
        )
          .then(async (response) => {
            if (response.OK) {
              me._coreLogService.debug(
                "CheckoutService.GetStoredCheckoutPaymentModel",
                "RequestService (response OK)",
                response
              );

              // Set is_remote_group_order from last order info if not already set by server
              if (response.Data.is_remote_group_order === undefined) {
                try {
                  const lastOrderInfo = await me.getLastOrderInfo();
                  if (lastOrderInfo) {
                    // Check if it's a group order from saved group_id or ordertype
                    response.Data.is_remote_group_order =
                      (lastOrderInfo.group_id && lastOrderInfo.group_id > 0) ||
                      lastOrderInfo.ordertype === orderTypes.deliveryGroup ||
                      lastOrderInfo.ordertype === orderTypes.pickupGroup;
                  } else {
                    response.Data.is_remote_group_order = false;
                  }
                } catch (error) {
                  me._coreLogService.error(
                    "CheckoutService.GetStoredCheckoutPaymentModel",
                    "Failed to determine if remote group order",
                    error
                  );
                  response.Data.is_remote_group_order = false;
                }
              }

              resolve(response.Data);
            } else {
              me._coreLogService.error(
                "CheckoutService.GetStoredCheckoutPaymentModel",
                "RequestService (response not OK)",
                response
              );
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => {
            me._coreLogService.error(
              "CheckoutService.GetStoredCheckoutPaymentModel",
              "RequestService",
              error
            );
            reject();
          });
      } catch (error) {
        me._coreLogService.error(
          "CheckoutService.GetStoredCheckoutPaymentModel",
          "try catch",
          error
        );
        reject();
      }
    });
  }

  public setLastOrderInfo(lastOrderInfo: LastOrderInfo): Promise<void> {
    return this._coreCOMService.environment.valueStorage.set(
      StorageHive.Local,
      LocalKeys.WebshopLastOrderInfo,
      lastOrderInfo
    );
  }

  public async getLastOrderInfo(): Promise<LastOrderInfo> {
    try {
      const lastOrderInfo =
        await this._coreCOMService.environment.valueStorage.get(
          StorageHive.Local,
          LocalKeys.WebshopLastOrderInfo
        );
      return lastOrderInfo != null ? lastOrderInfo : null;
    } catch (e) {
      this._coreLogService.error(
        "WebshopService.getLastOrderInfo",
        "valueStorage.get WebshopLastOrderInfo",
        e
      );
    }
  }

  /** Navigiert zum OrderResult  */
  public ShowFinalResult(result: CheckoutPaymentModel): void {
    const me = this;
    const done = result.done;
    me._coreLogService.info(
      "CheckoutService.ShowFinalResult",
      `ResponseCode: ${ResponseCode_Done[done]}(${done})`
    );
    if (
      done === ResponseCode_Done.Authorised ||
      done === ResponseCode_Done.Cancelled ||
      done === ResponseCode_Done.Error ||
      done === ResponseCode_Done.Refused
    ) {
      // In house hat eine andere Abschluss Seite
      if (result.ih) {
        // no order history for InHouse
        // route to inHouseOrderresult page
        me._ngZone.run(() => {
          me._routerHelperService.setRouteTargetDataByKey(
            "CheckoutPaymentModel",
            result
          );
          me._router.navigate(["/inHouseOrderresult"]);
        });
      } else {
        // refresh order history
        const orderHistoryObsServ =
          ObjectObserverFactory.GetObjectObserverByInjectionHelper<any>(
            ContentType.OrderHistory,
            me._objObsInjectHelper
          );
        orderHistoryObsServ.RefreshObjectsFromServer();
        // route to orderresult page
        me._ngZone.run(() => {
          me._routerHelperService.setRouteTargetDataByKey(
            "CheckoutPaymentModel",
            result
          );

          // Navigate to appropriate result page
          if (result.is_remote_group_order) {
            // For group orders, basket is cleared by RemoteGroupOrderService.cleanConnectionOnPaymentDone
            me._router.navigate(["/remoteOrderGroupPayResult"]);
          } else {
            // For normal orders, clear basket on successful payment
            if (result.done == ResponseCode_Done.Authorised) {
              me._webShopService.clearItems();
            }
            me._router.navigate(["/orderresult"]);
          }
        });
      }
    } else {
      throw Error(
        `Done: ${ResponseCode_Done[done]}(${done}) is not a known final state`
      );
    }
  }
}
